// src/pages/settings/CompanySettings.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  Paper,
  Divider,
  Alert,
  Avatar,
  Button
} from '@mui/material';
import {
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Language as WebsiteIcon,
  LocationOn as LocationIcon,
  Save as SaveIcon,
  Upload as UploadIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

// Components
import MagicCard from '../../components/ui/MagicCard';
import MagicButton from '../../components/ui/MagicButton';
import MagicTextField from '../../components/ui/FormFields/MagicTextField';
import MagicLoading from '../../components/ui/MagicLoading';

// Services
import { api } from '../../services/api';

const CompanySettings = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    name: '',
    logo: '',
    address: {
      street: '',
      city: '',
      state: '',
      country: '',
      postalCode: ''
    },
    phone: '',
    email: '',
    website: '',
    registrationNumber: '',
    taxId: '',
    bankDetails: {
      bankName: '',
      accountName: '',
      accountNumber: '',
      routingNumber: '',
      swiftCode: '',
      iban: ''
    },
    socialMedia: {
      facebook: '',
      twitter: '',
      instagram: '',
      linkedin: '',
      youtube: ''
    }
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      console.log('Loading company settings...');
      const response = await api.get('/settings/company');
      console.log('Load response:', response);
      if (response?.data?.settings) {
        setSettings(response.data.settings);
        console.log('Settings loaded:', response.data.settings);
      }
    } catch (error) {
      console.error('Failed to load company settings:', error);
      // If settings don't exist yet, use the default structure
      if (error?.response?.status === 404) {
        console.log('No settings found, using defaults');
        // Settings will remain as default
      } else {
        enqueueSnackbar('Failed to load company settings', { variant: 'error' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    console.log('Saving settings:', settings);
    setSaving(true);
    try {
      const response = await api.put('/settings/company', settings);
      console.log('Save response:', response);
      enqueueSnackbar('Company settings updated successfully', { variant: 'success' });
      // Reload settings to get the latest data
      await loadSettings();
    } catch (error) {
      console.error('Failed to update settings:', error);
      const errorMessage = error?.response?.data?.message || 'Failed to update settings';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      enqueueSnackbar('Logo file size must be less than 5MB', { variant: 'error' });
      return;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      enqueueSnackbar('Please upload an image file', { variant: 'error' });
      return;
    }

    const formData = new FormData();
    formData.append('logo', file);

    try {
      // Don't set Content-Type header - let axios set it with the boundary
      const response = await api.post('/settings/company/logo', formData);
      console.log('Logo upload response:', response);
      
      // Check both possible response structures
      const logoUrl = response?.data?.data?.logoUrl || response?.data?.logoUrl;
      console.log('Extracted logo URL:', logoUrl);
      
      if (logoUrl) {
        console.log('Setting new logo URL:', logoUrl);
        setSettings(prev => {
          const newSettings = {
            ...prev,
            logo: logoUrl
          };
          console.log('Updated settings:', newSettings);
          return newSettings;
        });
        enqueueSnackbar('Logo uploaded successfully', { variant: 'success' });
        // Don't reload settings - we already have the updated URL
      } else {
        console.error('Logo URL not found in response:', response?.data);
      }
    } catch (error) {
      console.error('Failed to upload logo:', error);
      const errorMessage = error?.response?.data?.message || 'Failed to upload logo';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  const handleFieldChange = (field, value) => {
    setSettings(prev => {
      const keys = field.split('.');
      if (keys.length === 1) {
        return { ...prev, [field]: value };
      }
      
      // Handle nested fields
      const newSettings = { ...prev };
      let current = newSettings;
      
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) {
          current[keys[i]] = {};
        }
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return newSettings;
    });
  };

  if (loading) {
    return <MagicLoading fullScreen message="Loading company settings..." />;
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Company Settings
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage your company information and branding
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Basic Information */}
        <Grid item xs={12} md={8}>
          <MagicCard>
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
              <Divider sx={{ mb: 3 }} />
              
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <MagicTextField
                    fullWidth
                    label="Company Name"
                    value={settings.name}
                    onChange={(e) => handleFieldChange('name', e.target.value)}
                    InputProps={{
                      startAdornment: <BusinessIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <MagicTextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={settings.email}
                    onChange={(e) => handleFieldChange('email', e.target.value)}
                    InputProps={{
                      startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <MagicTextField
                    fullWidth
                    label="Phone"
                    value={settings.phone}
                    onChange={(e) => handleFieldChange('phone', e.target.value)}
                    InputProps={{
                      startAdornment: <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <MagicTextField
                    fullWidth
                    label="Website"
                    value={settings.website}
                    onChange={(e) => handleFieldChange('website', e.target.value)}
                    InputProps={{
                      startAdornment: <WebsiteIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <MagicTextField
                    fullWidth
                    label="Registration Number"
                    value={settings.registrationNumber}
                    onChange={(e) => handleFieldChange('registrationNumber', e.target.value)}
                  />
                </Grid>

                <Grid item xs={12}>
                  <MagicTextField
                    fullWidth
                    label="Tax ID"
                    value={settings.taxId}
                    onChange={(e) => handleFieldChange('taxId', e.target.value)}
                  />
                </Grid>
              </Grid>
            </Box>
          </MagicCard>

          {/* Address */}
          <MagicCard sx={{ mt: 3 }}>
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Address
              </Typography>
              <Divider sx={{ mb: 3 }} />
              
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <MagicTextField
                    fullWidth
                    label="Street Address"
                    value={settings.address.street}
                    onChange={(e) => handleFieldChange('address.street', e.target.value)}
                    InputProps={{
                      startAdornment: <LocationIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <MagicTextField
                    fullWidth
                    label="City"
                    value={settings.address.city}
                    onChange={(e) => handleFieldChange('address.city', e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <MagicTextField
                    fullWidth
                    label="State/Province"
                    value={settings.address.state}
                    onChange={(e) => handleFieldChange('address.state', e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <MagicTextField
                    fullWidth
                    label="Country"
                    value={settings.address.country}
                    onChange={(e) => handleFieldChange('address.country', e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <MagicTextField
                    fullWidth
                    label="Postal Code"
                    value={settings.address.postalCode}
                    onChange={(e) => handleFieldChange('address.postalCode', e.target.value)}
                  />
                </Grid>
              </Grid>
            </Box>
          </MagicCard>

          {/* Bank Details */}
          <MagicCard sx={{ mt: 3 }}>
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Bank Details
              </Typography>
              <Divider sx={{ mb: 3 }} />
              
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <MagicTextField
                    fullWidth
                    label="Bank Name"
                    value={settings.bankDetails.bankName}
                    onChange={(e) => handleFieldChange('bankDetails.bankName', e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <MagicTextField
                    fullWidth
                    label="Account Name"
                    value={settings.bankDetails.accountName}
                    onChange={(e) => handleFieldChange('bankDetails.accountName', e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <MagicTextField
                    fullWidth
                    label="Account Number"
                    value={settings.bankDetails.accountNumber}
                    onChange={(e) => handleFieldChange('bankDetails.accountNumber', e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <MagicTextField
                    fullWidth
                    label="Routing Number"
                    value={settings.bankDetails.routingNumber}
                    onChange={(e) => handleFieldChange('bankDetails.routingNumber', e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <MagicTextField
                    fullWidth
                    label="SWIFT Code"
                    value={settings.bankDetails.swiftCode}
                    onChange={(e) => handleFieldChange('bankDetails.swiftCode', e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <MagicTextField
                    fullWidth
                    label="IBAN"
                    value={settings.bankDetails.iban}
                    onChange={(e) => handleFieldChange('bankDetails.iban', e.target.value)}
                  />
                </Grid>
              </Grid>
            </Box>
          </MagicCard>
        </Grid>

        {/* Logo and Branding */}
        <Grid item xs={12} md={4}>
          <MagicCard>
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Company Logo
              </Typography>
              <Divider sx={{ mb: 3 }} />
              
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                {console.log('Current logo URL:', settings.logo)}
                <Avatar
                  sx={{
                    width: 150,
                    height: 150,
                    bgcolor: 'background.default',
                    border: '2px dashed',
                    borderColor: 'divider'
                  }}
                  src={settings.logo}
                  key={settings.logo} // Force re-render when logo changes
                >
                  {!settings.logo && <BusinessIcon sx={{ fontSize: 60, color: 'text.disabled' }} />}
                </Avatar>
                
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="logo-upload"
                  type="file"
                  onChange={handleLogoUpload}
                />
                <label htmlFor="logo-upload">
                  <MagicButton
                    component="span"
                    variant="outlined"
                    startIcon={<UploadIcon />}
                    size="small"
                  >
                    Upload Logo
                  </MagicButton>
                </label>
                
                <Typography variant="caption" color="text.secondary" align="center">
                  Recommended: 500x500px, PNG or JPG format
                </Typography>
              </Box>
            </Box>
          </MagicCard>

          {/* Social Media */}
          <MagicCard sx={{ mt: 3 }}>
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Social Media
              </Typography>
              <Divider sx={{ mb: 3 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Facebook"
                    size="small"
                    value={settings.socialMedia.facebook}
                    onChange={(e) => handleFieldChange('socialMedia.facebook', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Twitter"
                    size="small"
                    value={settings.socialMedia.twitter}
                    onChange={(e) => handleFieldChange('socialMedia.twitter', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Instagram"
                    size="small"
                    value={settings.socialMedia.instagram}
                    onChange={(e) => handleFieldChange('socialMedia.instagram', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="LinkedIn"
                    size="small"
                    value={settings.socialMedia.linkedin}
                    onChange={(e) => handleFieldChange('socialMedia.linkedin', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="YouTube"
                    size="small"
                    value={settings.socialMedia.youtube}
                    onChange={(e) => handleFieldChange('socialMedia.youtube', e.target.value)}
                  />
                </Grid>
              </Grid>
            </Box>
          </MagicCard>
        </Grid>
      </Grid>

      {/* Save Button */}
      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button
          variant="outlined"
          onClick={() => {
            console.log('Test button clicked!');
            console.log('Current settings:', settings);
            console.log('Saving state:', saving);
          }}
        >
          Test Console
        </Button>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={() => {
            console.log('Save button clicked!');
            handleSave();
          }}
          disabled={saving}
          size="large"
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </Box>
    </Box>
  );
};

export default CompanySettings;